export interface Recipe {
  id: string;
  name: string;
  description: string;
  servings: number;
  prep_time: number;
  cook_time: number;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  cuisine_type: string;
  dietary_tags: string[];
  ingredients: RecipeIngredient[];
  instructions: string[];
  nutrition_per_serving: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
  };
  source: 'guru' | 'personal' | 'public_domain' | 'community';
  source_details: {
    guru_name?: string;
    book_title?: string;
    contributed_by?: string;
    scan_source?: string;
  };
  image_url?: string;
  rating: number;
  save_count: number;
  created_at: string;
  updated_at: string;
}

export interface RecipeIngredient {
  id: string;
  name: string;
  amount: number;
  unit: string;
  fdc_id?: number;
  ndc_code?: string;
  notes?: string;
  substitutions?: string[];
}

export interface RecipeContributor {
  id: string;
  name: string;
  avatar: string;
  bio: string;
  specialty: string;
  recipe_count: number;
  follower_count: number;
  rating: number;
  verified: boolean;
  badges: string[];
}

export const exampleRecipes: Recipe[] = [
  {
    id: '1',
    name: 'Mediterranean Quinoa Bowl',
    description: 'Fresh quinoa with roasted vegetables and lemon herb dressing',
    servings: 4,
    prep_time: 15,
    cook_time: 25,
    difficulty: 'Easy',
    cuisine_type: 'Mediterranean',
    dietary_tags: ['vegetarian', 'gluten-free', 'high-fiber'],
    ingredients: [
      { id: '1', name: 'Quinoa', amount: 1, unit: 'cup' },
      { id: '2', name: 'Cherry Tomatoes', amount: 2, unit: 'cups' },
      { id: '3', name: 'Cucumber', amount: 1, unit: 'medium' },
      { id: '4', name: 'Red Onion', amount: 0.5, unit: 'medium' },
      { id: '5', name: 'Olive Oil', amount: 2, unit: 'tbsp' },
      { id: '6', name: 'Lemon Juice', amount: 1, unit: 'tbsp' }
    ],
    instructions: [
      'Rinse quinoa thoroughly',
      'Cook quinoa according to package instructions',
      'Chop vegetables and mix with cooked quinoa',
      'Dress with olive oil and lemon juice',
      'Season with salt and pepper to taste'
    ],
    nutrition_per_serving: {
      calories: 280,
      protein: 8,
      carbs: 45,
      fat: 8,
      fiber: 6
    },
    source: 'guru',
    source_details: {
      guru_name: 'Dr. Sarah Mediterranean'
    },
    rating: 4.8,
    save_count: 1247,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: '2',
    name: 'Grilled Chicken Salad',
    description: 'Fresh greens with grilled chicken and vinaigrette',
    servings: 2,
    prep_time: 10,
    cook_time: 15,
    difficulty: 'Easy',
    cuisine_type: 'American',
    dietary_tags: ['high-protein', 'low-carb'],
    ingredients: [
      { id: '7', name: 'Chicken Breast', amount: 2, unit: 'pieces' },
      { id: '8', name: 'Mixed Greens', amount: 4, unit: 'cups' },
      { id: '9', name: 'Cherry Tomatoes', amount: 1, unit: 'cup' },
      { id: '10', name: 'Balsamic Vinegar', amount: 2, unit: 'tbsp' },
      { id: '11', name: 'Olive Oil', amount: 1, unit: 'tbsp' }
    ],
    instructions: [
      'Season chicken with salt and pepper',
      'Grill chicken until cooked through',
      'Toss greens with tomatoes',
      'Slice chicken and add to salad',
      'Dress with balsamic vinaigrette'
    ],
    nutrition_per_serving: {
      calories: 280,
      protein: 25,
      carbs: 12,
      fat: 15,
      fiber: 4
    },
    source: 'personal',
    source_details: {
      contributed_by: 'Your Recipe'
    },
    rating: 4.5,
    save_count: 892,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: '3',
    name: 'Keto Avocado Smoothie',
    description: 'Creamy avocado smoothie with coconut milk and protein',
    servings: 1,
    prep_time: 5,
    cook_time: 0,
    difficulty: 'Easy',
    cuisine_type: 'Keto',
    dietary_tags: ['keto', 'dairy-free', 'high-fat'],
    ingredients: [
      { id: '12', name: 'Avocado', amount: 0.5, unit: 'medium' },
      { id: '13', name: 'Coconut Milk', amount: 1, unit: 'cup' },
      { id: '14', name: 'Protein Powder', amount: 1, unit: 'scoop' },
      { id: '15', name: 'Spinach', amount: 1, unit: 'cup' },
      { id: '16', name: 'Stevia', amount: 1, unit: 'tsp' }
    ],
    instructions: [
      'Blend all ingredients until smooth',
      'Add ice if desired',
      'Serve immediately'
    ],
    nutrition_per_serving: {
      calories: 320,
      protein: 22,
      carbs: 8,
      fat: 25,
      fiber: 6
    },
    source: 'guru',
    source_details: {
      guru_name: 'Dr. Mike Keto'
    },
    rating: 4.7,
    save_count: 1563,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }
];

export const exampleContributors: RecipeContributor[] = [
  {
    id: '1',
    name: 'Dr. Sarah Mediterranean',
    avatar: '/avatars/sarah.jpg',
    bio: 'Registered dietitian specializing in Mediterranean cuisine and heart-healthy eating patterns.',
    specialty: 'Mediterranean Diet',
    recipe_count: 47,
    follower_count: 12450,
    rating: 4.9,
    verified: true,
    badges: ['Verified Expert', 'Top Contributor', 'Heart Health Specialist']
  },
  {
    id: '2',
    name: 'Chef Maria Garcia',
    avatar: '/avatars/maria.jpg',
    bio: 'Professional chef with 15 years of experience in healthy Latin cuisine.',
    specialty: 'Latin American Cuisine',
    recipe_count: 32,
    follower_count: 8920,
    rating: 4.8,
    verified: true,
    badges: ['Professional Chef', 'Cultural Cuisine Expert']
  },
  {
    id: '3',
    name: 'Dr. Mike Keto',
    avatar: '/avatars/mike.jpg',
    bio: 'Nutritionist and keto diet specialist helping people achieve metabolic health.',
    specialty: 'Ketogenic Diet',
    recipe_count: 28,
    follower_count: 15670,
    rating: 4.7,
    verified: true,
    badges: ['Keto Expert', 'Metabolic Health Specialist']
  }
]; 